import { useEffect, useState } from "react";
import api from "../services/api";
import { RefreshCcw } from "lucide-react";

export default function ActivityLogPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
    page: 1,
    limit: 200,
  });

  const [filters, setFilters] = useState({
    admin_id: "",
    cust_id: "",
    subject: "",
    action: "",
    date_from: "",
    date_to: "",
  });

  const fetchLogs = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");

      const { data } = await api.get("/act-log", {
        headers: {
          Authorization: token,
        },
        params: {
          page,
          limit: 200,
          ...filters,
        },
      });

      setRows(data.data || []);
      setPagination(data.pagination);
    } catch (err) {
      console.error(err);
      alert("Gagal mengambil activity log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const handleFilter = () => {
    setPage(1);
    fetchLogs();
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Activity Log
        </h1>

        <p className="text-sm text-gray-500 dark:text-gray-400">
          Riwayat aktivitas sistem
        </p>
      </div>

      {/* FILTER */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <input
            placeholder="Admin ID"
            value={filters.admin_id}
            onChange={(e) =>
              setFilters({
                ...filters,
                admin_id: e.target.value,
              })
            }
            className="border rounded px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
          />

          <input
            placeholder="Customer ID"
            value={filters.cust_id}
            onChange={(e) =>
              setFilters({
                ...filters,
                cust_id: e.target.value,
              })
            }
            className="border rounded px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
          />

          <input
            placeholder="Subject"
            value={filters.subject}
            onChange={(e) =>
              setFilters({
                ...filters,
                subject: e.target.value,
              })
            }
            className="border rounded px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
          />

          <input
            placeholder="Action"
            value={filters.action}
            onChange={(e) =>
              setFilters({
                ...filters,
                action: e.target.value,
              })
            }
            className="border rounded px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
          />

          <input
            type="date"
            value={filters.date_from}
            onChange={(e) =>
              setFilters({
                ...filters,
                date_from: e.target.value,
              })
            }
            className="border rounded px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
          />

          <input
            type="date"
            value={filters.date_to}
            onChange={(e) =>
              setFilters({
                ...filters,
                date_to: e.target.value,
              })
            }
            className="border rounded px-3 py-2 dark:bg-slate-700 dark:border-slate-600 dark:text-white"
          />
        </div>

        <div className="flex gap-2 mt-4">
          <button
            onClick={handleFilter}
            className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            Filter
          </button>

          <button
            onClick={fetchLogs}
            className="px-4 py-2 rounded bg-gray-600 text-white hover:bg-gray-700 flex items-center gap-2"
          >
            <RefreshCcw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-slate-700">
              <tr>
                <th className="p-3 text-left">ID</th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Admin</th>
                <th className="p-3 text-left">Customer</th>
                <th className="p-3 text-left">Subject</th>
                <th className="p-3 text-left">Action</th>
                <th className="p-3 text-left">Description</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-10 text-gray-500">
                    Tidak ada data
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-t dark:border-slate-700">
                    <td className="p-3">{row.id}</td>

                    <td className="p-3 whitespace-nowrap">
                      {new Date(row.created_at).toLocaleString()}
                    </td>

                    <td className="p-3">{row.admin_name}</td>

                    <td className="p-3">{row.customer_name || "-"}</td>

                    <td className="p-3">
                      <span className="px-2 py-1 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
                        {row.subject}
                      </span>
                    </td>

                    <td className="p-3">
                      <span className="px-2 py-1 rounded bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                        {row.action}
                      </span>
                    </td>

                    <td className="p-3 max-w-xl">{row.description}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="flex justify-between items-center p-4 border-t dark:border-slate-700">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Total: {pagination.total}
          </div>

          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-2 rounded border disabled:opacity-50 dark:border-slate-600"
            >
              Prev
            </button>

            <div className="px-3 py-2">
              {pagination.page} / {pagination.totalPages}
            </div>

            <button
              disabled={page >= pagination.totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-2 rounded border disabled:opacity-50 dark:border-slate-600"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
